import React from "react";
import useSWR from "swr";
import { useParams, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Gauge, Invoice, ShoppingCart, BookOpen, Buildings, FileText, GearSix } from "@phosphor-icons/react";
import { fetcher } from "./common";
import Saisie from "./Saisie";
import QuickEntry from "./QuickEntry";
import Reconciliation from "./Reconciliation";
import BankMatching from "./BankMatching";
import Lettrage from "./Lettrage";
import Cloture from "./Cloture";
import EtatsOhada from "./EtatsOhada";
import ImportCsv from "./ImportCsv";
import ChartBrowser from "./ChartBrowser";
import { LedgerTab, BalanceTab, TiersTab } from "./Reports";
import Settings from "./Settings";
import DossierDashboard from "./DossierDashboard";
import Invoicing from "./Invoicing";
import Purchases from "./Purchases";
import FixedAssets from "./FixedAssets";
import TaxDeclarations from "./TaxDeclarations";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: Gauge, testid: "sec-dashboard" },
  { id: "facturation", label: "Facturation", icon: Invoice, testid: "sec-facturation" },
  { id: "achats", label: "Achats", icon: ShoppingCart, testid: "sec-achats" },
  { id: "comptabilite", label: "Comptabilité", icon: BookOpen, testid: "sec-comptabilite" },
  { id: "immobilisations", label: "Immobilisations", icon: Buildings, testid: "sec-immobilisations" },
  { id: "etats", label: "États de synthèse", icon: FileText, testid: "sec-etats" },
  { id: "declarations", label: "Déclarations fiscales", icon: FileText, testid: "sec-declarations" },
  { id: "parametres", label: "Paramètres", icon: GearSix, testid: "sec-parametres" },
];

export default function AccountingFile() {
  const { fileId } = useParams();
  const { data: file } = useSWR(`/accounting/files/${fileId}`, fetcher);
  const { data: years = [] } = useSWR(`/accounting/files/${fileId}/years`, fetcher);
  const { data: journals = [] } = useSWR(`/accounting/files/${fileId}/journals`, fetcher);
  const [section, setSection] = React.useState("dashboard");

  // Si la section active devient invisible après un changement de mode, revenir au dashboard
  React.useEffect(() => {
    if (file?.accounting_mode === "tresorerie" && ["facturation", "achats"].includes(section)) {
      setSection("dashboard");
    }
  }, [file?.accounting_mode, section]);

  if (!file) return <div className="text-sm text-zinc-500">Chargement du dossier…</div>;

  // Mode trésorerie (système minimal OHADA) : les flux d'engagement (factures à émettre/payer) sont masqués
  const visibleSections = SECTIONS.filter(
    (s) => file.accounting_mode !== "tresorerie" || !["facturation", "achats"].includes(s.id)
  );

  return (
    <div>
      <div className="mb-6">
        <Link to="/comptabilite" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="acc-back">
          <ArrowLeft size={12} /> Tous les dossiers
        </Link>
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Dossier comptable</div>
          <h1 className="font-serif-display text-4xl leading-none tracking-tight" data-testid="accfile-title">{file.name}</h1>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200">{file.client?.name}</span>
            <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200">{file.chart_label}</span>
            <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200">{file.currency}</span>
            <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200">TVA {file.tva_rate}%</span>
            <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200" data-testid="accfile-mode">
              {file.accounting_mode === "tresorerie" ? "Trésorerie (SMT)" : "Engagement (système normal)"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-52 shrink-0 flex lg:flex-col gap-1 overflow-x-auto">
          {visibleSections.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              data-testid={s.testid}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm whitespace-nowrap text-left ${
                section === s.id ? "bg-[#2E3192] text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              <s.icon size={16} />
              <span>{s.label}</span>
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0">
          {section === "dashboard" && <DossierDashboard file={file} />}
          {section === "facturation" && <Invoicing file={file} />}
          {section === "achats" && <Purchases file={file} />}
          {section === "comptabilite" && (
            <Tabs defaultValue="saisie-simple">
              <TabsList className="flex flex-wrap h-auto gap-1 bg-zinc-100 p-1 rounded-md">
                <TabsTrigger value="saisie-simple" data-testid="tab-saisie-simple">Saisie simplifiée</TabsTrigger>
                <TabsTrigger value="saisie" data-testid="tab-saisie">Débit / Crédit</TabsTrigger>
                <TabsTrigger value="import" data-testid="tab-import">Import banque</TabsTrigger>
                <TabsTrigger value="rapprochement" data-testid="tab-rapprochement">Rapprochement</TabsTrigger>
                <TabsTrigger value="rapprochement-assiste" data-testid="tab-rapprochement-assiste">Rapprochement assisté</TabsTrigger>
                <TabsTrigger value="lettrage" data-testid="tab-lettrage">Lettrage</TabsTrigger>
                <TabsTrigger value="plan" data-testid="tab-plan">Plan comptable</TabsTrigger>
                <TabsTrigger value="livre" data-testid="tab-livre">Grand livre</TabsTrigger>
                <TabsTrigger value="balance" data-testid="tab-balance">Balance 6 colonnes</TabsTrigger>
                <TabsTrigger value="tiers" data-testid="tab-tiers">Tiers & âgée</TabsTrigger>
                <TabsTrigger value="cloture" data-testid="tab-cloture">Clôture</TabsTrigger>
              </TabsList>
              <div className="mt-6">
                <TabsContent value="saisie-simple"><QuickEntry file={file} /></TabsContent>
                <TabsContent value="saisie"><Saisie file={file} journals={journals} /></TabsContent>
                <TabsContent value="import"><ImportCsv file={file} /></TabsContent>
                <TabsContent value="rapprochement"><Reconciliation file={file} /></TabsContent>
                <TabsContent value="rapprochement-assiste"><BankMatching file={file} /></TabsContent>
                <TabsContent value="lettrage"><Lettrage file={file} /></TabsContent>
                <TabsContent value="plan"><ChartBrowser file={file} /></TabsContent>
                <TabsContent value="livre"><LedgerTab file={file} /></TabsContent>
                <TabsContent value="balance"><BalanceTab file={file} /></TabsContent>
                <TabsContent value="tiers"><TiersTab file={file} /></TabsContent>
                <TabsContent value="cloture"><Cloture file={file} years={years} /></TabsContent>
              </div>
            </Tabs>
          )}
          {section === "immobilisations" && <FixedAssets file={file} />}
          {section === "etats" && <EtatsOhada file={file} />}
          {section === "declarations" && <TaxDeclarations file={file} />}
          {section === "parametres" && <Settings file={file} years={years} journals={journals} fileId={fileId} />}
        </div>
      </div>
    </div>
  );
}

